import { useNavigate, useParams, useRouterState, useSearch } from "@tanstack/react-router";
import { ChevronRight, House } from "lucide-react";

import { useListFolders } from "../../api/generated/endpoints/teams/teams";
import { FolderCrumbs } from "../folders/FolderCrumbs";
import { useMembership } from "../membership";
import { managesBrands } from "../permissions";
import { SECTIONS } from "./nav";
import { targetRoot, useCurrentSection, useNavBrands } from "./nav-links";
import classes from "./shell.module.css";

/**
 * The library's folder scope (spec A3b): the brand's folders and the URL's folder, and a way to
 * show another; `undefined` off the library, where the scope reads "All Assets" alone.
 */
function useLibraryFolders(teamId: string, onLibrary: boolean) {
  const brandId = useParams({ strict: false, select: (params) => params.brandId });
  const folderId = useSearch({ strict: false, select: (search) => search.folder });
  const navigate = useNavigate();
  const folders = useListFolders(teamId, brandId ?? "", {
    query: { enabled: onLibrary && brandId !== undefined },
  });
  if (!onLibrary || folders.data === undefined) return undefined;
  const choose = (folder: string | undefined) =>
    void navigate({
      to: ".",
      // Another folder's view starts flat, as live's does.
      search: ((current: Record<string, unknown>) => ({
        ...current,
        folder,
        nested: undefined,
        scope: undefined,
      })) as never,
      replace: true,
    });
  return { folders: folders.data, folderId, choose };
}

/**
 * Live's middle crumb on a form page: "Create" on a new item, "Edit" on a saved one. Read from the
 * matched route's id, as the section is, so no feature is imported.
 */
function useStep(teamId: string): string | undefined {
  const routeId = useRouterState({ select: (state) => state.matches.at(-1)?.routeId });
  const me = useMembership(teamId);
  if (routeId === undefined) return undefined;
  if (routeId.endsWith("/new")) return "Create";
  if (routeId.endsWith("/edit")) return "Edit";
  // A product's page is its form only for those who manage brands; others read it (A5 11).
  if (routeId.endsWith("/products/$productId") && me !== undefined && managesBrands(me.role)) {
    return "Edit";
  }
  return undefined;
}

/**
 * The header's crumbs (design-system spec 2.2): the page's section with its nav icon, then the
 * folder scope, "All Assets" unless the library is on a folder, where live's project crumbs walk
 * the folder's path (spec A3b); a form page puts "Create" or
 * "Edit" between them, as live does. They come from the matched route
 * and nav.ts, never from path segments (which would read "Teams > Brands > Generate").
 */
export function Breadcrumbs({ teamId }: { teamId: string }) {
  const section = useCurrentSection();
  const { linkBrandId } = useNavBrands(teamId);
  const library = useLibraryFolders(teamId, section === "Library");
  const step = useStep(teamId);
  const item = section === undefined ? undefined : SECTIONS.get(section);
  if (section === undefined || item === undefined) return null;
  const Icon = section === "Home" ? House : item.icon;
  const label = (
    <>
      <Icon size={20} strokeWidth={1.5} aria-hidden />
      {/* Live's crumbs name the studio pages in the plural (decision 57). */}
      {item.crumb ?? item.label}
    </>
  );
  const href = targetRoot(item.target, teamId, linkBrandId);
  return (
    <nav aria-label="Breadcrumbs" className={classes.crumbs}>
      {href === undefined ? (
        <span className={classes.crumb}>{label}</span>
      ) : (
        href({ className: classes.crumb, children: label })
      )}
      <ChevronRight size={18} strokeWidth={1.5} className={classes.crumbSeparator} aria-hidden />
      {step === undefined ? null : (
        <>
          <span className={classes.crumb}>{step}</span>
          <ChevronRight
            size={18}
            strokeWidth={1.5}
            className={classes.crumbSeparator}
            aria-hidden
          />
        </>
      )}
      {library === undefined ? (
        <span className={classes.crumb} aria-current="page">
          All Assets
        </span>
      ) : (
        <FolderCrumbs
          folders={library.folders}
          folderId={library.folderId}
          onChoose={library.choose}
          crumbClass={classes.crumb ?? ""}
          separatorClass={classes.crumbSeparator ?? ""}
        />
      )}
    </nav>
  );
}
